import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../lib/cn.ts';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-[var(--font-text)] font-medium text-sm',
    'rounded-md border border-transparent',
    'transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'cursor-pointer select-none',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-[var(--color-charcoal)] text-[var(--color-alabaster)]',
          'border-[var(--color-charcoal)]',
          'hover:bg-[var(--color-stone-700)]',
        ],
        secondary: [
          'bg-[var(--color-canvas)] text-[var(--color-charcoal)]',
          'border-[var(--color-warm-stone-soft)]',
          'hover:bg-[var(--color-stone-100)] hover:border-[var(--color-warm-stone)]',
        ],
        ghost: ['bg-transparent text-[var(--color-charcoal)]', 'hover:bg-[var(--color-canvas)]'],
        destructive: [
          'bg-[var(--color-alert)] text-[var(--color-alabaster)]',
          'border-[var(--color-alert)]',
          'hover:opacity-90',
        ],
        outline: [
          'bg-transparent text-[var(--color-charcoal)]',
          'border-[var(--color-warm-stone-soft)]',
          'hover:bg-[var(--color-canvas)]',
        ],
      },
      size: {
        sm: 'h-8 px-3 text-xs gap-1.5',
        md: 'h-9 px-4',
        lg: 'h-11 px-6 text-base',
        icon: 'h-9 w-9 p-0',
        'icon-sm': 'h-8 w-8 p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  'data-testid'?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
